<template>
  <header-index />
    <!-- 面包屑导航 -->
    <div class="px-4 h-[60px] leading-[60px]" style="background-color: rgb(245, 245, 245);">
        <div class="flex mx-auto items-center text-sm mb-6 leading-[60px] container max-auto">

            <nuxt-link to="/" class="text-gray-500 hover:text-green-600">
                <span class="icon-home mr-1"></span> Home
            </nuxt-link>
            <span class="mx-2 text-gray-500">></span>
            <nuxt-link to="/packaging" class="text-gray-500 hover:text-green-600">Packaging</nuxt-link>
            <span class="mx-2 text-gray-500">></span>
            <span class="text-gray-700">FAQ</span>
        </div>
    </div>
  <div class="container mx-auto py-8 px-4 min-h-screen">
    <div class="flex flex-col md:flex-row">
      <!-- 左侧 FAQ 内容区域 -->
      <div class="w-full md:w-3/4 pr-0 md:pr-8">
        <div class="bg-white p-6 shadow-md rounded-md">
          <h2 class="text-2xl font-weight-600 mb-8">{{ currentCategory!.title || 'Popular Articles' }}</h2>
          
          <!-- FAQ 手风琴组件 -->
          <div class="space-y-6">
            <div v-for="(faq, index) in currentFaqs" :key="`faq-${currentCategoryId}-${index}`" class="border-b border-gray-200 pb-5">
              <div class="flex justify-between items-center cursor-pointer py-2 faq-title-row" @click="toggleFaq(index)">
                <h3 class="text-gray-800 text-lg faq-question" :class="{'font-weight-600': openFaqs[index], 'font-weight-400': !openFaqs[index]}">
                  <span class="bullet-point mr-3 inline-block w-3 h-3 text-gray-700">•</span>
                  {{ faq.question }}
                </h3>
                <span class="font-weight-600 text-xl text-gray-700 toggle-btn">
                  {{ openFaqs[index] ? '-' : '+' }}
                </span>
              </div>
              
              <transition name="faq-toggle">
                <div v-if="openFaqs[index]" class="faq-content mt-4 bg-gray-100 p-5 rounded text-gray-600">
                  <p class="faq-answer" v-html="faq.answer"></p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 右侧分类过滤区域 -->
      <div class="w-full md:w-1/4 mt-6 md:mt-0">
        <div class="space-y-3">
          <a v-for="category in categories" :key="category.id" 
             href="javascript:void(0)" 
             @click="selectCategory(category.id)"
             :class="['block py-3 px-4 rounded text-sm transition duration-300', 
                     currentCategoryId === category.id 
                       ? 'bg-green-600 text-white' 
                       : 'bg-gray-100 hover:bg-green-600 hover:text-white']">
            {{ category.name }}
          </a>
        </div>
      </div>
    </div>
  </div>
  <footer-index />
</template>

<script setup lang="ts" name="SinglePageFAQS">
  import type { SinglePage } from '~/types';
  import cmsUtils from '~/utils/cmsUtils';
  import headerIndex from '../../components/header/header-index.vue'
  import footerIndex from '../../components/footer/footer-index.vue'
  import { ref, computed, onMounted } from 'vue'
  
  const props = defineProps<{
    singlePage: SinglePage
  }>()

  const cmsInfo = await cmsUtils.getCmsInfo()
  const route = useRoute()
  const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
  const canonicalUrl = baseUrl + route.path.replace('.html','')
  
  
  // 当前选择的类别ID
  const currentCategoryId = ref(1);
  
  // 跟踪打开状态的响应式数组
  const openFaqs = ref<boolean[]>([]);
  
  // FAQ数据接口定义
  interface FAQItem {
    question: string;
    answer: string;
    isOpen: boolean;
  }
  
  interface CategoryData {
    [key: number]: FAQItem[];
  }
  
  // 所有FAQ数据，按类别组织（保持不变）
  const faqsByCategory: CategoryData = {
    // 内容保持不变
    1: [
      {
        question: 'What choices affect my pricing?',
        answer: '<p class="mb-4">Here are some of the choices that may affect the price of your packaging:</p><ul class="faq-list"><li>Size (larger packaging requires more sheets of material to be used)</li><li>Quantity (ordering higher quantities will net you a lower cost per unit)</li><li>Material (premium materials will cost more)</li><li>Additional processes (additional processes require extra work)</li><li>Finish (premium finishes will cost more)</li></ul>',
        isOpen: false
      },
      {
        question: 'What is the turnaround time on my order?',
        answer: 'Standard turnaround time is typically 2-3 weeks after artwork approval.<br><br>Rush options may be available for an additional fee.',
        isOpen: false
      },
      {
        question: 'Where do you ship to?',
        answer: 'We ship globally with various shipping options available depending on your location and requirements.',
        isOpen: false
      },
      {
        question: 'Do you have volume discounts or price breaks?',
        answer: 'Yes, we offer volume discounts on larger orders.<br><br>Please contact our sales team for specific pricing tiers.',
        isOpen: false
      },
      {
        question: 'Do you offer rush orders?',
        answer: 'Yes, rush orders are available for an additional fee depending on the complexity and volume of your order.',
        isOpen: false
      },
      {
        question: 'How do I place a reorder?',
        answer: 'You can place a reorder by logging into your account or by contacting our customer service team with your previous order details.',
        isOpen: false
      },
      {
        question: 'I have more questions',
        answer: 'Please contact our customer service team at sales@leaflypackaging.com or call us at (+86) 158 1559 7437 for any additional questions.',
        isOpen: false
      }
    ],
    2: [
      {
        question: 'What is the standard turnaround time?',
        answer: 'Our standard turnaround time is 2-3 weeks from the date of artwork approval and payment confirmation.',
        isOpen: false
      },
      {
        question: 'Do you offer rush production?',
        answer: 'Yes, we offer rush production services with expedited turnaround times of 7-10 business days for an additional fee. Please contact our customer service for pricing and availability.',
        isOpen: false
      },
      {
        question: 'What affects printing turnaround time?',
        answer: '<p class="mb-4">Several factors can affect printing turnaround time:</p><ul class="faq-list"><li>Order complexity and quantity</li><li>Special finishes or treatments</li><li>Current production schedule</li><li>Shipping location and method</li><li>Artwork readiness and approval speed</li></ul>',
        isOpen: false
      },
      {
        question: 'How do you handle print proofs?',
        answer: 'We provide digital proofs for all orders at no additional cost. Physical proofs are available for an additional fee and will add 5-7 days to your production timeline.',
        isOpen: false
      }
    ],
    3: [
      {
        question: 'What shipping carriers do you use?',
        answer: 'We primarily ship via UPS, FedEx, and DHL for international shipments. For larger orders, we may use freight shipping services.',
        isOpen: false
      },
      {
        question: 'How are shipping costs calculated?',
        answer: 'Shipping costs are calculated based on package dimensions, weight, destination, and selected shipping speed. We provide exact shipping quotes during checkout.',
        isOpen: false
      },
      {
        question: 'Do you ship internationally?',
        answer: 'Yes, we ship to most countries worldwide. International shipping times typically range from 7-21 business days depending on the destination and customs processing.',
        isOpen: false
      },
      {
        question: 'Is expedited shipping available?',
        answer: 'Yes, we offer expedited shipping options including overnight, 2-day, and 3-day delivery where available. Additional fees apply for expedited services.',
        isOpen: false
      }
    ],
    4: [
      {
        question: 'How are invoices delivered?',
        answer: 'Invoices are sent electronically via email upon order confirmation. Physical copies can be included with your shipment upon request.',
        isOpen: false
      },
      {
        question: 'What payment terms do you offer?',
        answer: 'For new customers, we require 100% payment upfront. For established accounts, we offer Net-30 terms with approved credit application.',
        isOpen: false
      },
      {
        question: 'How do I request a copy of a past invoice?',
        answer: 'You can request copies of past invoices by emailing our accounting department at accounting@leafly.com or by calling our customer service line.',
        isOpen: false
      }
    ],
    5: [
      {
        question: 'What payment methods do you accept?',
        answer: '<p class="mb-4">We accept the following payment methods:</p><ul class="faq-list"><li>Credit cards (Visa, Mastercard, American Express)</li><li>Bank transfers/ACH</li><li>Wire transfers</li><li>Company checks (for established customers only)</li></ul>',
        isOpen: false
      },
      {
        question: 'Do you offer payment plans?',
        answer: 'For large orders exceeding $10,000, we may offer split payment options with 50% due at order confirmation and 50% due before shipping. This is subject to approval.',
        isOpen: false
      },
      {
        question: 'Is my payment information secure?',
        answer: 'Yes, all payment information is processed through PCI-compliant secure systems. We never store your complete credit card information on our servers.',
        isOpen: false
      }
    ],
    6: [
      {
        question: 'What is your return policy?',
        answer: 'As we create custom products to your specifications, we generally don\'t accept returns. However, if there is a manufacturing defect or error on our part, we will provide replacements or refunds.',
        isOpen: false
      },
      {
        question: 'How do I report a problem with my order?',
        answer: 'Please contact our customer service team within 7 days of receiving your order if you encounter any issues. Include your order number and photos of any defects.',
        isOpen: false
      },
      {
        question: 'Do you offer refunds?',
        answer: 'Refunds are issued only in cases where we cannot provide a replacement or if there was a significant error on our part. Each case is evaluated individually.',
        isOpen: false
      }
    ],
    7: [
      {
        question: 'What materials do you use for cannabis packaging?',
        answer: '<p class="mb-4">We use a variety of materials for cannabis packaging including:</p><ul class="faq-list"><li>Child-resistant plastics</li><li>Recyclable paperboard</li><li>Glass containers</li><li>Biodegradable materials</li><li>Tin containers</li><li>Sustainable hemp-based packaging</li></ul>',
        isOpen: false
      },
      {
        question: 'Are your products compliant with cannabis regulations?',
        answer: 'Yes, our packaging solutions are designed to meet state-specific cannabis packaging regulations. We stay updated on changing regulations to ensure compliance.',
        isOpen: false
      },
      {
        question: 'What is child-resistant packaging?',
        answer: 'Child-resistant packaging meets the standards set by the Consumer Product Safety Commission (CPSC) and requires special mechanisms that make it difficult for children under 5 years old to open, while remaining accessible for adults.',
        isOpen: false
      }
    ],
    8: [
      {
        question: 'What custom printing options do you offer?',
        answer: '<p class="mb-4">We offer a wide range of custom printing options:</p><ul class="faq-list"><li>Digital printing for short runs and variable data</li><li>Offset printing for larger quantities and precise color matching</li><li>Foil stamping and embossing</li><li>Spot UV coating</li><li>Soft touch finishes</li><li>Custom die-cutting</li></ul>',
        isOpen: false
      },
      {
        question: 'Do you offer design services?',
        answer: 'Yes, our in-house design team can help create packaging designs from scratch or refine your existing artwork to ensure it\'s production-ready. Design services are billed at an hourly rate.',
        isOpen: false
      },
      {
        question: 'What file formats do you accept for artwork?',
        answer: 'We accept Adobe Illustrator (.ai), Photoshop (.psd), InDesign (.indd), and print-ready PDFs. All fonts should be outlined or included, and images should be at least 300 DPI.',
        isOpen: false
      },
      {
        question: 'What is the minimum order quantity?',
        answer: 'Our minimum order quantities vary by product type. Custom printed boxes typically start at 500 units, while labels and smaller items may have lower minimums of 250 units.',
        isOpen: false
      }
    ]
  };
  
  // 分类标签
  const categories = ref([
    { id: 1, name: 'All FAQ', title: 'Popular Articles' },
    { id: 2, name: 'Printing & Turnaround', title: 'Printing & Turnaround FAQs' },
    { id: 3, name: 'Shipping', title: 'Shipping Information' },
    { id: 4, name: 'Invoices & How It\'s', title: 'Invoices & Billing FAQs' },
    { id: 5, name: 'ePayments & Invoices', title: 'Payment Information' },
    { id: 6, name: 'eCommerce & Returns', title: 'Returns & Policies' },
    { id: 7, name: 'General Knowledge', title: 'Cannabis Packaging Knowledge' },
    { id: 8, name: 'Products & Services', title: 'Our Products & Services' },
  ]);
  
  // 当前选中的类别
  const currentCategory = computed(() => {
    return categories.value.find(cat => cat.id === currentCategoryId.value) || categories.value[0];
  });
  
  // 当前显示的FAQ列表
  const currentFaqs = computed(() => {
    const faqs = faqsByCategory[currentCategoryId.value] || [];
    
    // 每次列表变化时，重置openFaqs数组
    setTimeout(() => {
      initializeOpenStates(faqs);
    }, 0);
    
    return faqs;
  });
  
  // 初始化打开状态数组
  function initializeOpenStates(faqs: FAQItem[]) {
    openFaqs.value = faqs.map(faq => faq.isOpen || false);
    // 确保至少第一个FAQ是打开的
    if (faqs.length > 0) {
      openFaqs.value[0] = true;
    }
  }
  
  // 选择类别函数
  function selectCategory(id: number) {
    currentCategoryId.value = id;
    const faqs = faqsByCategory[id] || [];
    
    // 重置所有FAQ的打开状态
    initializeOpenStates(faqs);
  }
  
  // 切换FAQ函数
  function toggleFaq(index: number) {
    if (index >= 0 && index < openFaqs.value.length) {
      openFaqs.value[index] = !openFaqs.value[index];
    }
  }

  // 将所有 FAQ 展平用于 JSON-LD
  const allFaqFlat = Object.values(faqsByCategory).flat()
  useHead({
      script: [
          {
              type: 'application/ld+json',
              innerHTML: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: allFaqFlat.map(faq => ({
                      '@type': 'Question',
                      name: faq.question,
                      acceptedAnswer: {
                          '@type': 'Answer',
                          text: faq.answer.replace(/<[^>]*>/g, '')
                      }
                  }))
              })
          }
      ]
  })

  
  // 在挂载时初始化状态
  onMounted(() => {
    const faqs = faqsByCategory[currentCategoryId.value] || [];
    initializeOpenStates(faqs);
  });
</script>

<style scoped lang='scss'>
.icon-home {
  display: inline-block;
  width: 14px;
  height: 14px;

  background-size: contain;
  background-repeat: no-repeat;
  vertical-align: middle;
}

.faq-title-row {
  transition: color 0.3s ease;

  &:hover {
    .faq-question, 
    .bullet-point, 
    .toggle-btn {
      color: #059669 !important;
    }
  }
}

.faq-question {
  transition: color 0.3s ease;
}

.min-h-screen {
  min-height: calc(100vh - 200px); /* 减去头部和底部的高度 */
}

/* 过渡动画效果 */
.faq-toggle-enter-active,
.faq-toggle-leave-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  max-height: 1000px;
  opacity: 1;
  transform-origin: top;
  transform: translateY(0);
}

.faq-toggle-enter-from,
.faq-toggle-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  transform: translateY(-10px);
}

.faq-content {
  line-height: 1.6;
  font-size: 15px;
  overflow: hidden;
}

.faq-answer {
  color: #4B5563;
}

.faq-list {
  list-style-type: none;
  padding: 0;
  
  li {
    padding: 8px 0;
    line-height: 1.5;
  }
  
  li:not(:last-child) {
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
}

:deep(p) {
  margin-bottom: 1rem;
  line-height: 1.6;
}

:deep(br) {
  content: "";
  margin: 0.5rem 0;
  display: block;
}

.toggle-btn {
  padding: 0 8px;
  transition: color 0.3s ease;
}

.bullet-point {
  transition: color 0.3s ease;
}
</style>
